import * as Product from './product';
import { ConfigurableProperties, ProductAttributes } from '@dora/backpack/';
import { pipe } from 'fp-ts/lib/function';
import * as O from 'fp-ts/Option';

export type ProductVariantWithAttributes = {
    productId: string;
    attributes: Partial<Record<ProductAttributes.AttributeType, string>>;
    thumbnail: string;
};

export const findAttributesForVariant = (
    variantOptions: ProductVariantWithAttributes[] | null,
    variantId: string | undefined,
) => {
    return variantOptions?.find(opt => opt.productId === variantId)?.attributes;
};

export const isAttributeSelected = (
    variantOptions: ProductVariantWithAttributes[] | null,
    attributeType: ProductAttributes.AttributeType,
    attributeId: string,
    variantId: string | undefined,
): boolean => {
    const properties = findAttributesForVariant(variantOptions, variantId);
    if (properties) return properties[attributeType] === attributeId;
    else return false;
};

export const findNewVariant = (
    variantOptions: Array<ProductVariantWithAttributes>,
    attributeType: ProductAttributes.AttributeType,
    attributeId: string,
    currentVariantAttributes: Partial<Record<ProductAttributes.AttributeType, string>>,
) => {
    const attributeNotChanging = Object.entries(currentVariantAttributes).find(
        ([key, _]) => key !== attributeType,
    );

    if (attributeNotChanging) {
        const [attributeTypeNotChanging, attributeIdNotChanging] = attributeNotChanging;
        const compatibleOption = variantOptions.find(
            opt =>
                opt.attributes[attributeType] === attributeId &&
                opt.attributes[attributeTypeNotChanging as ProductAttributes.AttributeType] ===
                    attributeIdNotChanging,
        );

        if (compatibleOption) {
            return compatibleOption;
        }
    }

    const firstOptionMatchingOptionSelection = variantOptions.find(
        opt => opt.attributes[attributeType] === attributeId,
    );

    return firstOptionMatchingOptionSelection;
};

export const isAttributeCompatible = (
    attributeType: ProductAttributes.AttributeType,
    attributeId: string,
    variantOptions: Array<ProductVariantWithAttributes>,
    currentVariant?: string,
) => {
    return pipe(
        O.Do,
        O.bind('variantId', () => O.fromNullable(currentVariant)),
        O.bind('activeAttributes', ({ variantId }) =>
            O.fromNullable(variantOptions.find(opt => opt.productId === variantId)?.attributes),
        ),
        O.bind('attributeNotChanging', ({ activeAttributes }) =>
            O.fromNullable(
                Object.entries(activeAttributes).find(([key, _]) => key !== attributeType),
            ),
        ),
        O.fold(
            () => true,
            ({ attributeNotChanging }) => {
                const [attributeTypeNotChanging, attributeIdNotChanging] = attributeNotChanging;
                return (
                    variantOptions.find(
                        opt =>
                            opt.attributes[attributeType] === attributeId &&
                            opt.attributes[
                                attributeTypeNotChanging as ProductAttributes.AttributeType
                            ] === attributeIdNotChanging,
                    ) !== undefined
                );
            },
        ),
    );
};

export const sortVariantAttributes = (atts: Array<ProductAttributes.Attribute>) => {
    return [...atts].sort((a, b) => {
        if (a.attributeType === 'dimension' && b.attributeType === 'dimension') {
            // Dimension Options are sorted by value ascending, prioritising width (X), then height (Y) then depth (Z)
            return a.x.value > b.x.value
                ? 1
                : a.x.value < b.x.value
                ? -1
                : a.y.value > b.y.value
                ? 1
                : a.y.value < b.y.value
                ? -1
                : a.z.value > b.z.value
                ? 1
                : a.z.value < b.z.value
                ? -1
                : 0;
        } else {
            // Finish options are sorted alphabetically
            return a.displayName > b.displayName ? 1 : -1;
        }
    });
};

const configurableByFinishAndDimensionsOnly = (configurableProperties: {
    [x: string]: {
        isGlobal: boolean;
        label: string;
        type: string;
    };
}) => {
    return !Object.keys(configurableProperties).some(
        property => !['finish', 'dimension'].includes(property),
    );
};

const buildVariantAttributes = (
    product: Product.Product,
    attributes: ProductAttributes.AttributeProperties,
): Record<ProductAttributes.AttributeType, string> => {
    const allAttributes = [
        ...(attributes.dimension?.properties ?? []),
        ...(attributes.finish?.properties ?? []),
    ];

    const configurableProperties = Object.keys(product.configurableProperties ?? {}).filter(key =>
        ProductAttributes.isAttributeType(key),
    );

    return configurableProperties.reduce((atts, p) => {
        if (p === 'finish') {
            const propVal = product.properties['finish'];
            if (ConfigurableProperties.isFinish(propVal)) {
                const attributeId = allAttributes.find(
                    att =>
                        att.attributeType === 'finish' &&
                        att.hex === propVal.colour &&
                        att.displayName === propVal.name,
                )?.id;
                if (attributeId) atts.finish = attributeId;
            }
        } else if (p === 'dimension') {
            const propVal = product.properties['dimension'];
            if (ConfigurableProperties.isDimension(propVal)) {
                const attributeId = allAttributes.find(
                    att =>
                        att.attributeType === 'dimension' &&
                        att.x.value === propVal.x.value &&
                        att.y.value === propVal.y.value &&
                        att.z.value === propVal.z.value,
                )?.id;
                if (attributeId) atts.dimension = attributeId;
            }
        }
        return atts;
    }, {} as Record<ProductAttributes.AttributeType, string>);
};

const buildProductAttributes = (products: Array<Product.Product>) => {
    return products.reduce((attributes, product, i) => {
        const configurableProperties = product.configurableProperties;

        if (
            configurableProperties &&
            configurableByFinishAndDimensionsOnly(configurableProperties)
        ) {
            const finishProperty = product.properties['finish'];
            const dimensionProperty = product.properties['dimension'];

            if (
                finishProperty &&
                configurableProperties['finish'] &&
                ConfigurableProperties.isFinish(product.properties['finish'])
            ) {
                const finishLabel = configurableProperties['finish'].label;
                const newFinishAttribute: ProductAttributes.FinishAttribute = {
                    displayName: finishProperty.name,
                    id: `${finishProperty.name}-${i}`,
                    attributeType: 'finish',
                    hex: finishProperty.colour!,
                };

                if (attributes['finish']) {
                    const alreadyExists = (
                        attributes['finish'].properties as Array<ProductAttributes.FinishAttribute>
                    ).find(
                        prop =>
                            prop.displayName === newFinishAttribute.displayName &&
                            prop.hex === newFinishAttribute.hex,
                    );
                    if (!alreadyExists) {
                        attributes['finish'].properties.push(newFinishAttribute);
                    }
                } else {
                    attributes['finish'] = {
                        displayName: finishLabel,
                        properties: [newFinishAttribute],
                    };
                }
            }
            if (
                dimensionProperty &&
                configurableProperties['dimension'] &&
                ConfigurableProperties.isDimension(product.properties['dimension'])
            ) {
                const dimensionLabel = configurableProperties['dimension'].label;
                const newDimensionAttribute: ProductAttributes.DimensionAttribute = {
                    displayName: `${dimensionProperty.x.value}${dimensionProperty.x.unit} x ${dimensionProperty.y.value}${dimensionProperty.y.unit}`,
                    id: `${dimensionProperty.x.value}x${dimensionProperty.y.value}x${dimensionProperty.z.value}-${i}`,
                    attributeType: 'dimension',
                    x: dimensionProperty.x,
                    y: dimensionProperty.y,
                    z: dimensionProperty.z,
                };

                if (attributes['dimension']) {
                    const alreadyExists = (
                        attributes['dimension']
                            .properties as Array<ProductAttributes.DimensionAttribute>
                    ).find(prop =>
                        ProductAttributes.dimensionAttributesEqual(prop, newDimensionAttribute),
                    );
                    if (!alreadyExists) {
                        attributes['dimension'].properties.push(newDimensionAttribute);
                    }
                } else {
                    attributes['dimension'] = {
                        displayName: dimensionLabel,
                        properties: [newDimensionAttribute],
                    };
                }
            }
        }

        return attributes;
    }, {} as ProductAttributes.AttributeProperties);
};

export function attributesFromProducts(products: Array<Product.Product>) {
    const attributes = buildProductAttributes(products);

    const variantOptions = products.reduce((opts, p) => {
        if (p.kind === 'simple') {
            if (
                p.configurableProperties &&
                configurableByFinishAndDimensionsOnly(p.configurableProperties)
            ) {
                opts.push({
                    productId: p.sku,
                    thumbnail: p.thumbnail ?? '',
                    attributes: buildVariantAttributes(p, attributes),
                });
            }
        }
        return opts;
    }, [] as Array<ProductVariantWithAttributes>);

    return {
        variantOptions,
        attributes,
    };
}
